import { MessageSquare } from "lucide-react";
import { Link, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { useHealth, useLogout, useMe } from "@/lib/api";

// Constant-length JSX expression keeps prettier output stable for any rendered name.
const PROJECT_NAME = "Spare Parts ID";

export function HomePage() {
  const navigate = useNavigate();
  const logoutMutation = useLogout();
  const { data: health } = useHealth();
  const { data: me } = useMe();

  const backendStatus = health?.status ?? "unreachable";

  const handleLogout = async () => {
    await logoutMutation.mutateAsync();
    navigate("/login");
  };

  return (
    <div className="bg-background min-h-screen">
      <header className="border-border bg-card/90 flex items-center justify-between border-b px-6 py-4 backdrop-blur">
        <div className="flex items-center gap-4">
          <img src="/brand/logo.png" alt="Norican" className="h-8 w-auto" />
          <div>
            <p className="font-heading text-foreground text-sm font-semibold">{PROJECT_NAME}</p>
            <p className="text-muted text-xs">AI spare parts identification workspace</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/profile">
            <Button variant="ghost" size="sm">
              Profile
            </Button>
          </Link>
          {me?.role === "admin" && (
            <>
              <Link to="/admin/users">
                <Button variant="ghost" size="sm">
                  Admin
                </Button>
              </Link>
              <Link to="/admin/activity">
                <Button variant="ghost" size="sm">
                  Activity
                </Button>
              </Link>
            </>
          )}
          <Button variant="outline" onClick={handleLogout}>
            Sign out
          </Button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-6 py-12">
        <div className="from-accent to-accent/95 overflow-hidden rounded-3xl bg-gradient-to-br p-8 text-white md:p-12">
          <div className="max-w-3xl">
            <p className="mb-4 text-sm font-semibold tracking-[0.24em] text-white/60 uppercase">
              Norican field service intelligence
            </p>
            <h2 className="font-heading text-3xl font-semibold md:text-5xl">
              Turn technician notes, machine details and photos into the correct Part ID faster.
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-white/80 md:text-base">
              Search internal service knowledge with AI assistance so teams can identify spare parts
              confidently across machine types, field conditions and maintenance history.
            </p>
            <div className="mt-6">
              <Link to="/select-brand">
                <Button size="lg" className="gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Open Chat
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          <div className="bg-card rounded-2xl border border-[var(--color-border)] p-6">
            <p className="text-primary text-sm font-semibold">Upload service context</p>
            <p className="text-muted mt-2 text-sm">
              Capture reports, images and machine data from technicians in the field.
            </p>
          </div>
          <div className="bg-card rounded-2xl border border-[var(--color-border)] p-6">
            <p className="text-primary text-sm font-semibold">Search trusted knowledge</p>
            <p className="text-muted mt-2 text-sm">
              Use AI to navigate manuals, historical cases and internal expertise quickly.
            </p>
          </div>
          <div className="bg-card rounded-2xl border border-[var(--color-border)] p-6">
            <p className="text-primary text-sm font-semibold">Resolve the right Part ID</p>
            <p className="text-muted mt-2 text-sm">
              Reduce downtime with faster spare part identification tied to real service context.
            </p>
          </div>
        </div>
        <div className="text-muted mt-8 text-sm">
          <p className="text-muted mt-2 text-sm">
            Backend status:{" "}
            <span className={backendStatus === "ok" ? "text-green-600" : "text-red-500"}>
              {backendStatus}
            </span>
          </p>
        </div>
      </main>
    </div>
  );
}
